import {TemplateNode} from "#template/template-node/template-node"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {fundamentalTypeList} from "#template/fundamental/registry"
import {NodesInput} from "#template/fundamental/input/nodes-input"
import type {ParametersParameters} from "#template/nodes/parameters"
import type {TemplateInstance} from "#template/nodes/object/template-instance"
import type {EvaluatedTemplateInput, EvaluatedTemplateInputs, NodesEvaluatedInput} from "#template/fundamental/evaluated-input"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NotReady} from "#template/runtime-graph/slots"
import type {TemplateContext} from "#template/compile-context"
import {getInterfaceIdPrefix} from "#template/interface-descriptors"
import type {CompactUIDTable} from "@cm/utils"

type EvaledInputEntry = BuilderInlet<EvaluatedTemplateInput> | undefined | typeof NotReady

export class NodeEvaluator {
    readonly resultCache = new Map<TemplateNode, BuilderInlet<unknown>>()

    constructor(
        readonly idMap: CompactUIDTable<string>,
        readonly templateScope: GraphBuilderScope,
        readonly templateContext: TemplateContext,
        readonly ambientInputs: EvaluatedTemplateInputs,
        readonly activeNodeSet: Set<TemplateNode> | undefined,
    ) {}

    getLocalId(node: TemplateNode): string {
        return this.idMap.intern(node.instanceId)
    }

    getScope(node: TemplateNode): GraphBuilderScope {
        return this.templateScope.scope(`${node.nodeClass}@${this.getLocalId(node)}`)
    }

    getEvalScope(node: TemplateNode): GraphBuilderScope {
        return this.templateScope.scope(`Eval-${node.nodeClass}@${this.getLocalId(node)}`)
    }

    // Overload 1: simple node-or-raw — `NoInfer<R>` keeps R inferred from the node branch only.
    // Picked first by TS overload resolution, so single-branch unions (e.g. `StringLike = string |
    // StringLikeNode`) and generic-P parameters (e.g. `NonNullable<P["x"]>`) stay simple.
    // Overload 2: distributive conditional — handles multi-branch unions like `ScalarArg = number |
    // NumberLikeNode | GeometryValueNode` where the first overload would pick one branch's R and
    // reject the others. The conditional yields the union of every branch's R.
    evaluate<R>(node: EvaluableTemplateNode<R> | NoInfer<R>): BuilderInlet<R>
    evaluate<T>(node: T): BuilderInlet<T extends EvaluableTemplateNode<infer R> ? R : T>
    evaluate(node: EvaluableTemplateNode<unknown> | unknown): BuilderInlet<unknown> {
        if (!(node instanceof TemplateNode)) return node
        const cached = this.resultCache.get(node)
        if (cached !== undefined) return cached
        const out = (node as EvaluableTemplateNode<unknown>).evaluate(this.getEvalScope(node), this)
        this.resultCache.set(node, out)
        return out
    }

    evaluateTemplateInputsForParameters(
        scope: GraphBuilderScope,
        parameters: ParametersParameters,
        id: string,
    ): readonly [BuilderInlet<EvaluatedTemplateInputs>, string[]] {
        const evaledInputs: {[inputId: string]: EvaledInputEntry} = {}
        const claimedInputIds: string[] = []
        const stringInputs: BuilderInlet<EvaluatedTemplateInput>[] = []

        for (const [inputId, value] of Object.entries(parameters)) {
            if (value === undefined || value === null) continue
            claimedInputIds.push(inputId)

            if (Array.isArray(value) && value.every((v) => v instanceof TemplateNode)) {
                const evalScope = scope.scope(inputId)
                evaledInputs[inputId] = scope.struct<NodesEvaluatedInput>("EvaluatedTemplateInput", {
                    type: "nodes",
                    ancestry: ["nodes"],
                    value: NodesInput.evaluateList(evalScope, this, value),
                    origin: undefined,
                })
                continue
            }

            const origin = value instanceof TemplateNode ? value : undefined
            let dispatched: BuilderInlet<EvaluatedTemplateInput> | null = null
            for (const ft of fundamentalTypeList) {
                const result = ft.makeEvaluatedInput(scope, this, value, origin)
                if (result !== null) {
                    dispatched = result
                    if (ft.name === "String") stringInputs.push(result)
                    break
                }
            }

            if (dispatched !== null) {
                evaledInputs[inputId] = dispatched
            } else if (value instanceof TemplateNode) {
                throw new Error(`Cannot use node ${value.nodeClass} as input for a template`)
            } else {
                evaledInputs[inputId] = {type: "unknown", ancestry: ["unknown"], value, origin: undefined}
            }
        }

        for (const [inputId, entry] of Object.entries(this.ambientInputs)) {
            const [prefix, unwrappedId] = getInterfaceIdPrefix(inputId)
            if (prefix === id) evaledInputs[unwrappedId] = entry
        }

        const inputs = scope.group("evaledInput", evaledInputs) as BuilderInlet<EvaluatedTemplateInputs>
        if (stringInputs.length === 0) return [inputs, claimedInputIds] as const

        // A string input can drive a sub-template's ConfigGroup selection, which is read during the eager
        // onFilterActive pass. The lenient `group` emits before its NotReady fields resolve, so a
        // still-NotReady string would reach the ConfigGroup prematurely. Thus, we gate the
        // whole inputs object on them being fully evaluated, while keeping the rest lazy so unrelated
        // sub-template instances still eager-load.
        const gatedInputs = scope.pureLambda(scope.tuple(inputs, scope.list(stringInputs, "stringInputs")), ([inputs]) => inputs, "gatedTemplateInputs")
        return [gatedInputs, claimedInputIds] as const
    }

    // Convenience over `evaluateTemplateInputsForParameters` for the common case of a whole template instance.
    evaluateTemplateInputs(scope: GraphBuilderScope, templateInstance: TemplateInstance): readonly [BuilderInlet<EvaluatedTemplateInputs>, string[]] {
        return this.evaluateTemplateInputsForParameters(scope, templateInstance.parameters.parameters.parameters, templateInstance.parameters.id)
    }
}
